import { useContext, useState } from 'react';

import { Or2Button } from '@/components/button/Or2Button';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { graphql, type DocumentType } from '@/gql';
import { useMutation } from '@/hooks/mutationWithSnackbar';
import { CurrentUserContext } from '@/context/CurrentUserContext';
import { removePendingInviteFromCache } from '@/modules/invitations/utils/pendingInvitesCache';

const DECLINE_PENDING_MEMBER_INVITE_MUTATION = graphql(`
  mutation DeclinePendingMemberInvite(
    $params: DeclinePendingMemberInviteInput
  ) {
    declinePendingMemberInvite(params: $params) {
      group {
        _id
      }
    }
  }
`);

const DECLINE_PENDING_INVITE_BUTTON_GROUP_FRAGMENT = graphql(`
  fragment declinePendingInviteButtonGroupFields on Group {
    _id
    name
  }
`);

interface Props {
  group: DocumentType<typeof DECLINE_PENDING_INVITE_BUTTON_GROUP_FRAGMENT>;
  onDeclined?: (() => void) | undefined;
}

export const DeclinePendingInviteButton = ({ group, onDeclined }: Props) => {
  const { _id: groupId, name } = group;
  const { currentUserId } = useContext(CurrentUserContext);

  const [dialogOpen, setDialogOpen] = useState(false);

  const [declinePendingMemberInvite, { loading }] = useMutation(
    DECLINE_PENDING_MEMBER_INVITE_MUTATION,
    {
      completionMessage: `Invitation to ${name ?? 'the board'} declined`,
      update: (cache) => {
        removePendingInviteFromCache(cache, currentUserId, groupId);
      },
      onCompleted: () => {
        setDialogOpen(false);
        onDeclined?.();
      },
    },
  );

  return (
    <>
      <Or2Button color="error" size="small" onClick={() => setDialogOpen(true)}>
        Decline
      </Or2Button>
      <ConfirmDialog
        open={dialogOpen}
        ariaLabel="decline-invitation"
        titleText="Decline invitation"
        innerText={`Decline your invitation to join ${name ?? 'this board'}?`}
        confirmText="Decline invitation"
        cancelText="Keep invitation"
        confirmButtonProps={{ color: 'error' }}
        loading={loading}
        onConfirm={() =>
          declinePendingMemberInvite({ variables: { params: { groupId } } })
        }
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
};
